/* סיכום תקופה ומבט שנתי: מה נכנס, מה יצא, איך נסגרה היתרה, ומה בלט מול תקופה רגילה */
import { memo } from './state.js';
import { absAmt, iso10, dDays, todayISO, median } from '../lib/format.js';
import { curKey, keyAdd, periodStart } from './periods.js';
import { filterActive, sumBy, txOfPeriod } from './tx.js';
import { cat } from './categories.js';
import { periodOpen, periodBalKnown } from './balance.js';
import { budgetsFor } from './budgets.js';
import { isSavingTx } from './savings.js';

const byCat = (txs) => {
  const m = {};
  filterActive(txs).forEach((t) => { if (t.type === 'expense') { const c = cat('expense', t.cat).id || 'other'; m[c] = (m[c] || 0) + absAmt(t); } });
  return m;
};

/** הכנסות, הוצאות ויתרה של תקופה אחת */
export function periodTotals(k) {
  const txs = txOfPeriod(k), act = filterActive(txs);
  const inc = sumBy(act, 'income'), exp = sumBy(act, 'expense');
  const closed = k < curKey();
  // לפני נקודת הפתיחה ובלי סריקות עו"ש מאז, היתרה לא ידועה
  const known = periodBalKnown(k);
  const close = known && closed ? periodOpen(keyAdd(k, 1)) : null;
  const open = known ? periodOpen(k) : null;
  return { k, inc, exp, net: inc - exp, open, close, n: txs.length, hasData: txs.length > 0, closed };
}

/** סיכום מלא של תקופה, מול ממוצע 3 התקופות שלפניה (רק כאלה שיש בהן נתונים) */
export function periodReview(k) {
  return memo('review|' + k, () => {
    const T = periodTotals(k);
    const prev = [1, 2, 3].map((i) => keyAdd(k, -i)).filter((p) => txOfPeriod(p).length);
    const avgExp = prev.length ? prev.reduce((s, p) => s + periodTotals(p).exp, 0) / prev.length : 0;
    const cur = byCat(txOfPeriod(k));
    const hist = prev.map((p) => byCat(txOfPeriod(p)));
    const avgOf = (c) => (hist.length ? hist.reduce((s, h) => s + (h[c] || 0), 0) / hist.length : 0);
    const cats = Object.keys(cur).map((c) => ({ c, v: cur[c], avg: avgOf(c) })).sort((a, b) => b.v - a.v);
    // עלייה של 20% ולפחות 200 ש"ח מעל הממוצע. "אחר" לא נחשב, כי הוא לא אומר כלום
    const rising = hist.length ? cats.filter((x) => x.c !== 'other' && x.v - x.avg >= 200 && x.v > x.avg * 1.2).slice(0, 3) : [];
    const falling = hist.length ? cats.filter((x) => x.c !== 'other' && x.avg - x.v >= 200 && x.v < x.avg * 0.8).slice(0, 2) : [];
    const overBudget = budgetsFor(k).map((g) => ({ c: g.cat, budget: Number(g.amount), spent: cur[g.cat] || 0 })).filter((g) => g.spent > g.budget);
    const big = filterActive(txOfPeriod(k)).filter((t) => t.type === 'expense').sort((a, b) => absAmt(b) - absAmt(a)).slice(0, 3);
    const saved = txOfPeriod(k).filter((t) => isSavingTx(t)).reduce((s, t) => s + (t.type === 'income' ? -1 : 1) * absAmt(t), 0);
    return { ...T, avgExp, nPrev: prev.length, cats, rising, falling, overBudget, big, saved };
  });
}

/** 12 התקופות האחרונות (כולל הנוכחית, שעוד לא נגמרה), מהישנה לחדשה */
export function yearData() {
  return memo('year', () => {
    const now = curKey(), rows = [];
    for (let i = 11; i >= 0; i--) rows.push(periodTotals(keyAdd(now, -i)));
    // לא מציגים חודשים ריקים לפני שהתחלת להשתמש באפליקציה
    const first = rows.findIndex((r) => r.hasData);
    return first < 0 ? [] : rows.slice(first);
  });
}

/** התקופה הקודמת, אם יש בה נתונים ועוד לא ראו את הסיכום שלה. מוצג בשבוע וחצי הראשונים של תקופה */
export function pendingReviewKey(seenKey, daysIntoPeriod) {
  const k = keyAdd(curKey(), -1);
  if (seenKey === k || daysIntoPeriod > 10 || !txOfPeriod(k).length) return null;
  return k;
}

/** קטגוריות שבתקופה הנוכחית יצא בהן הרבה יותר מהרגיל עד אותו יום: לפחות 30% ו-300 ₪ מעל החציון
    של התקופות הקודמות באותו שלב. במקום יעדי תקציב ידניים: ההשוואה היא למה שבאמת רגיל אצלכם */
export function catSpikes() {
  return memo('catSpikes', () => {
    const k = curKey(), day = dDays(todayISO(), periodStart(k)) + 1;
    const prev = [1, 2, 3].map((i) => keyAdd(k, -i)).filter((q) => txOfPeriod(q).some((t) => t.type === 'expense'));
    if (prev.length < 2) return [];
    const upTo = (q) => {
      const qs = periodStart(q), m = {};
      filterActive(txOfPeriod(q)).forEach((t) => {
        if (t.type !== 'expense' || dDays(iso10(t.date), qs) >= day) return;
        const c = cat('expense', t.cat).id || 'other';
        m[c] = (m[c] || 0) + absAmt(t);
      });
      return m;
    };
    const cur = upTo(k), hist = prev.map(upTo);
    return Object.keys(cur).filter((c) => c !== 'other')
      .map((c) => ({ c, v: cur[c], usual: median(hist.map((h) => h[c] || 0)) }))
      .filter((x) => x.v - x.usual >= 300 && x.v >= x.usual * 1.3)
      .sort((a, b) => (b.v - b.usual) - (a.v - a.usual))
      .slice(0, 3);
  });
}
